import React, { Fragment, ReactNode, useEffect, useMemo, useRef, useState, MouseEvent, useCallback } from 'react';
import './FmtpTable.scss';

import {
    Table,
    ColumnFiltersState,
    flexRender,
    getCoreRowModel,
    Row,
    SortingState,
    useReactTable,
    getExpandedRowModel,
    getFacetedRowModel,
    getFacetedUniqueValues,
    getFacetedMinMaxValues,
    Header,
    Cell,
    CellContext,
    HeaderContext,
    ExpandedState,
    OnChangeFn,
    RowSelectionState,
    getSortedRowModel,
    getFilteredRowModel,
    RowData,
    ColumnDef,
    InitialTableState,
    CoreRow,
    Column,
    Updater,
    ColumnFilter,
    ColumnSort,
} from '@tanstack/react-table';
import { Virtualizer, useVirtualizer } from '@tanstack/react-virtual';
import LoadingIndicator from 'src/components/common/LoadingIndicator/LoadingIndicator';
import IndeterminateCheckbox from './IndeterminiteCheckbox';
import { FetchNextPageOptions, InfiniteData, InfiniteQueryObserverResult } from '@tanstack/react-query';
import { FilterOption } from './Filters';
import { SortDirection, Permissions } from '@lanterds/types';
import { filterVariant } from './types';
import { Button, Box, Typography } from '@mui/material';
import { exportTableData } from 'src/util/exportTableData';
import { getUserEmailFromToken } from 'src/util/helpers';
import { isAuthorized } from 'src/util/Permissions';

export type FmtpData<T> = {
    value: T;
};

type TextAlign = 'left' | 'center' | 'right';

declare module '@tanstack/react-table' {
    interface TableMeta<TData extends RowData> {
        updateData?: (row: Row<TData>, column: Column<TData, unknown>, value: unknown) => void;
    }
    // Define custom properties for our columns here
    interface ColumnMeta<TData extends RowData, TValue> {
        isExpandable?: boolean;
        innerHtml?: (
            props: CellContext<TData, TValue>,
            style?: {
                textAlign: TextAlign;
            },
        ) => unknown;
        filterPlaceholder?: string;
        sortingOptionName?: keyof TData;
        headerHtml?: (
            props: HeaderContext<TData, unknown>,
            displayText: string | undefined,
            style?: {
                textAlign: TextAlign;
            },
        ) => React.JSX.Element | React.ReactNode | string;
        filterVariant?: filterVariant;
        style?: {
            textAlign: TextAlign;
        };
        headerText?: string;
    }
}

export interface FmtpTableProps<T extends object> {
    data: T[];
    onDataUpdate?: (row: Row<T>, column: Column<T, unknown>, value: unknown) => void;
    fetchNextPage?: (
        options?: FetchNextPageOptions | undefined,
    ) => Promise<InfiniteQueryObserverResult<InfiniteData<Response, unknown>, Error>> | undefined;
    isFetching?: boolean;
    columns: ColumnDef<T>[];
    fetchMoreOnBottomReached?: (containerRefElement?: HTMLDivElement | null) => void;
    virtualizerSettings?: {
        count?: number;
        getScrollElement?: () => HTMLDivElement;
        estimateSize?: () => number;
        overscan?: number;
        debug?: boolean;
    };
    onFilterChange?: (name: string, value: string | number | boolean) => void;
    // TODO: Need to figure this type out. Handling async is causing complications.
    onExpandedChange?: any; // OnChangeFn<ExpandedState>;
    onSortChange?: (header: Header<T, unknown>) => void;
    setSelectedRows?: OnChangeFn<RowSelectionState>;
    selectedRows?: RowSelectionState;
    enableRowSelection?: (row: Row<T>) => boolean;
    getSubRows?: (row: T) => T[] | undefined;
    initialSortingState?: ColumnSort[];
    initialFilterState?: ColumnFilter[];
    createExpandRows?: (row: Row<T>) => React.JSX.Element[];
    expanded?: ExpandedState;
    enableExpanding?: boolean;
    enableSorting?: boolean;
    isResizable?: boolean;
    enableFilters?: boolean;
    isVirtualized?: boolean;
    clearFilters?: boolean;
    manualExpanding?: boolean;
    clearSelections?: boolean;
    rowClickHandler?: (data: any) => void;
    loadingIndicatorMessage?: string;
    containerStyles?: React.CSSProperties;
    showLoading?: boolean;
    initialState?: InitialTableState;
    showTable?: boolean;
    contextMenuColumnNames?: string[];
    onContextMenu?: (e: MouseEvent, row: CoreRow<T>) => void;
    enableExport?: boolean;
    exportFileName?: string;
    totalResults?: number;
    // Temp fix for dynamic table resizing, this will be addressed in the UI overhaul
    occupiedSpaceBelow?: number;
    disableSelectAllHeader?: boolean;
}

const FmtpTable = <T extends object>({
    data,
    onFilterChange,
    onExpandedChange,
    onSortChange,
    columns: columnsParam,
    selectedRows,
    fetchMoreOnBottomReached = () => null,
    isVirtualized = false,
    virtualizerSettings,
    setSelectedRows,
    enableRowSelection,
    getSubRows = undefined,
    createExpandRows,
    rowClickHandler = () => null,
    expanded,
    initialSortingState = [],
    initialFilterState = [],
    enableExpanding = false,
    enableSorting = false,
    enableFilters = false,
    isResizable = false,
    isFetching = false,
    clearFilters = false,
    manualExpanding = false,
    clearSelections = false,
    loadingIndicatorMessage = 'Getting data...',
    containerStyles,
    showLoading = true,
    initialState,
    showTable = true,
    onDataUpdate,
    contextMenuColumnNames = [],
    onContextMenu,
    enableExport = false,
    exportFileName,
    totalResults,
    occupiedSpaceBelow,
    disableSelectAllHeader,
}: FmtpTableProps<T>) => {
    const loggedInUserEmail = getUserEmailFromToken();
    const exportDataAuthorized = isAuthorized(loggedInUserEmail, Permissions.exportData);
    // Reference to the scrolling element for scrolling logic
    const tableContainerRef = useRef<HTMLDivElement>(null);
    const tableRef = useRef<HTMLTableElement>(null);

    const [sorting, setSorting] = useState<SortingState>(initialSortingState);
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(initialFilterState);
    const [isExporting, setIsExporting] = useState(false);

    const tableColumns = useMemo<ColumnDef<T>[]>(() => {
        const columns = [];

        const select = {
            header: ({ table }: { table: Table<T> }) => {
                if (disableSelectAllHeader) {
                    return null;
                }
                return (
                    <IndeterminateCheckbox
                        checked={table.getIsAllRowsSelected()}
                        indeterminate={table.getIsSomeRowsSelected()}
                        onChange={table.getToggleAllRowsSelectedHandler()}
                    />
                );
            },
            cell: ({ row }: { row: Row<T>; table: Table<T> }) => (
                <div className="checkbox-cell">
                    <IndeterminateCheckbox
                        {...{
                            checked: row.getIsSelected(),
                            disabled: !row.getCanSelect(),
                            indeterminate: row.getIsSomeSelected(),
                            onChange: row.getToggleSelectedHandler(), // onRowSelectionChange(table, row) as React.FormEventHandler,
                        }}
                    />
                </div>
            ),
        };

        for (let i = 0; i < columnsParam.length; i++) {
            const column = columnsParam[i];
            // TODO: Should allow overriding every property?
            const { id, header, enableSorting, enableColumnFilter, meta } = column;
            const { isExpandable, innerHtml, headerHtml, style } = meta ?? {};

            const newColumn = {
                ...column,
                enableSorting,
                enableColumnFilter,
                cell: (props: CellContext<T, unknown>) => {
                    if (id === 'select') return select.cell(props);
                    const { getValue, row } = props;
                    const cellContent = innerHtml ? innerHtml(props, style) : getValue();
                    return isExpandable ? addExpandable(cellContent as ReactNode, row) : cellContent;
                },
                header: (props: HeaderContext<T, unknown>) => {
                    if (id === 'select') return select.header(props);
                    return headerHtml ? headerHtml(props, header?.toString(), style) : header;
                },
                meta: {
                    ...meta,
                    headerText: header?.toString(),
                },
            } as ColumnDef<T>;

            columns.push(newColumn);
        }
        return columns;
    }, [columnsParam]);

    const customSetSorting = (updater: Updater<SortingState>) => {
        setSorting((prev) => {
            const newSorting = typeof updater === 'function' ? updater(prev) : updater;
            return newSorting.map((sort) => {
                const column = reactTable.getColumn(sort.id);
                // Use sortingOptionName if it exists
                const sortingOptionName = (column?.columnDef.meta?.sortingOptionName as string) ?? sort.id;
                return { id: sortingOptionName, desc: sort.desc };
            });
        });
    };

    const reactTable = useReactTable({
        data,
        columns: tableColumns,
        enableSorting,
        enableExpanding,
        enableFilters,
        initialState: initialState,
        state: {
            sorting,
            expanded,
            columnFilters,
            rowSelection: selectedRows,
        },
        onRowSelectionChange: setSelectedRows,
        onColumnFiltersChange: setColumnFilters,
        onExpandedChange: onExpandedChange,
        getExpandedRowModel: getExpandedRowModel(),
        manualExpanding: manualExpanding,
        onSortingChange: (updater) => customSetSorting(updater),
        getCoreRowModel: getCoreRowModel(),
        getFacetedRowModel: getFacetedRowModel(),
        enableRowSelection,
        getFacetedUniqueValues: getFacetedUniqueValues(),
        getFacetedMinMaxValues: getFacetedMinMaxValues(),
        columnResizeMode: 'onChange',
        getSubRows: getSubRows,
        ...(!onSortChange ? { getSortedRowModel: getSortedRowModel() } : {}),
        ...(!onFilterChange ? { getFilteredRowModel: getFilteredRowModel() } : {}),
        meta: {
            ...(onDataUpdate ? { updateData: onDataUpdate } : null),
        },
    });

    useEffect(() => {
        if (clearFilters) {
            reactTable.resetRowSelection(clearFilters);
            reactTable.resetColumnFilters(clearFilters);
            reactTable.resetSorting(clearFilters);
        }
    }, [reactTable, clearFilters]);

    useEffect(() => {
        if (clearSelections) {
            reactTable.resetRowSelection(clearSelections);
        }
    }, [reactTable, clearSelections]);

    useEffect(() => {
        // Set initial height
        updateTableContainerHeight();

        // Resize observer approach
        const resizeObserver = new ResizeObserver(() => updateTableContainerHeight());
        resizeObserver.observe(document.body);

        // Cleanup observer on unmount
        return () => resizeObserver.disconnect();
    }, [data, expanded]);

    const updateTableContainerHeight = useCallback(() => {
        requestAnimationFrame(() => {
            if (tableContainerRef.current && tableRef.current) {
                const containerRect = tableContainerRef.current.getBoundingClientRect();
                if (data.length) {
                    const tableRect = tableRef.current.getBoundingClientRect();
                    const docHeight = document.documentElement.clientHeight;
                    const containerTop = containerRect.top;
                    const availableHeight = docHeight - containerTop - (occupiedSpaceBelow ?? 0);

                    if (tableRect.height < availableHeight) {
                        document.documentElement.style.setProperty(
                            '--container-height',
                            `${tableRect.height + (occupiedSpaceBelow ?? 0) + 20}px`,
                        );
                        document.documentElement.style.removeProperty('--container-offset');
                    }

                    if (availableHeight < tableContainerRef.current.scrollHeight) {
                        document.documentElement.style.setProperty(
                            '--container-offset',
                            `${containerRect.top + (occupiedSpaceBelow ?? 0)}px`,
                        );
                        document.documentElement.style.removeProperty('--container-height');
                    }
                } else {
                    // No data so show full table for message
                    document.documentElement.style.setProperty(
                        '--container-offset',
                        `${containerRect.top + (occupiedSpaceBelow ?? 0)}px`,
                    );
                    document.documentElement.style.removeProperty('--container-height');
                }
            }
        });
    }, [tableRef, tableContainerRef, data, occupiedSpaceBelow]);

    let virtualizer = null as Virtualizer<HTMLDivElement, Element> | null;

    if (isVirtualized) {
        virtualizer = useVirtualizer({
            count: reactTable.getRowModel().rows.length,
            getScrollElement: () => tableContainerRef.current,
            estimateSize: () => 100,
            // Sets number of rows above and below visible table to give buffer when scrolling
            overscan: 10,
            //measure dynamic row height, except in firefox because it measures table border height incorrectly
            measureElement:
                typeof window !== 'undefined' && navigator.userAgent.indexOf('Firefox') === -1
                    ? (element) => element?.getBoundingClientRect().height
                    : undefined,
            ...virtualizerSettings,
        });
    }

    const getTreeContent = (row: Row<T>, content: ReactNode) => {
        if (row.parentId) {
            const parentRow = row.getParentRow();

            return (
                <Box className="tree-branch">
                    <Box className="top"></Box>
                    <Box
                        className={`bottom ${
                            row.parentId && parentRow && Number(row.id.split('.')[1]) === parentRow.subRows.length - 1
                                ? 'last-leaf'
                                : ''
                        }`}
                    ></Box>
                </Box>
            );
        } else {
            return <>{content}</>;
        }
    };

    const addExpandable = useCallback((content: ReactNode, row: Row<T>) => {
        return (
            <>
                {row.subRows && row.subRows.length ? (
                    <>
                        <div className="body-cell-expandable">
                            <div className="expand-icon-container">
                                <button
                                    {...{
                                        onClick: row.getToggleExpandedHandler(),
                                        style: { cursor: 'pointer' },
                                        className: 'expandButton',
                                    }}
                                >
                                    {row.getIsExpanded() ? (
                                        <span className="material-icons">arrow_drop_down</span>
                                    ) : (
                                        <span className="material-icons">arrow_right</span>
                                    )}
                                </button>
                            </div>
                            <>{content}</>
                        </div>
                    </>
                ) : (
                    <>{content}</>
                )}
            </>
        );
    }, []);

    const contextMenuProps = useCallback(
        (cell: Cell<T, unknown>) => {
            if (onContextMenu && contextMenuColumnNames.includes(cell.column.id)) {
                return {
                    onContextMenu: (e: MouseEvent) => onContextMenu(e, cell.row),
                    style: { cursor: 'context-menu' },
                };
            }
            return null;
        },
        [contextMenuColumnNames, onContextMenu],
    );

    const createVirtualizedBody = useCallback(() => {
        if (virtualizer) {
            const paddingTop = virtualizer.getVirtualItems().length > 0 ? virtualizer.getVirtualItems()?.[0]?.start || 0 : 0;
            const paddingBottom =
                virtualizer.getVirtualItems().length > 0
                    ? virtualizer.getTotalSize() -
                      (virtualizer.getVirtualItems()?.[virtualizer.getVirtualItems().length - 1]?.end || 0)
                    : 0;

            return (
                <>
                    {paddingTop > 0 && (
                        <tr>
                            <td style={{ height: `${paddingTop}px` }} />
                        </tr>
                    )}
                    {virtualizer.getVirtualItems().map((virtualRow) => {
                        const { rows } = reactTable.getRowModel();
                        const row = rows[virtualRow.index] as Row<T>;
                        {
                            /* TODO: Try to merge this with createBody() */
                        }
                        return (
                            <Fragment key={row.id}>
                                <tr
                                    className={`body-row ${virtualRow.index % 2 === 0 ? 'odd' : 'even'}`}
                                    onClick={() => rowClickHandler(row.original)}
                                >
                                    {row.getVisibleCells().map((cell) => {
                                        const cellName = cell.id.split('_').slice(-1);
                                        return (
                                            <td
                                                key={cell.id}
                                                className={`body-cell ${cellName}-body-cell`}
                                                {...contextMenuProps(cell)}
                                            >
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </td>
                                        );
                                    })}
                                </tr>
                                {row.getIsExpanded() && manualExpanding && createExpandRows && createExpandRows(row)}
                            </Fragment>
                        );
                    })}
                    {paddingBottom > 0 && (
                        <tr>
                            <td style={{ height: `${paddingBottom}px` }} />
                        </tr>
                    )}
                </>
            );
        } else {
            return null;
        }
    }, [manualExpanding, createExpandRows, flexRender, contextMenuProps, reactTable]);

    const createBody = useCallback(() => {
        if (virtualizer) {
            return createVirtualizedBody();
        } else {
            const { rows } = reactTable.getRowModel();
            return rows.map((row: Row<T>) => {
                return (
                    <Fragment key={row.id}>
                        <tr key={row.id} className={'body-row'} onClick={() => rowClickHandler(row.original)}>
                            {row.getVisibleCells().map((cell: Cell<T, unknown>) => {
                                const cellName = cell.id.split('_').slice(-1);
                                return (
                                    <td key={cell.id} className={`body-cell body-cell-${cellName}`} {...contextMenuProps(cell)}>
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </td>
                                );
                            })}
                        </tr>
                        {row.getIsExpanded() && manualExpanding && createExpandRows && createExpandRows(row)}
                    </Fragment>
                );
            });
        }
    }, [virtualizer, reactTable, manualExpanding, createExpandRows, contextMenuProps, rowClickHandler, flexRender]);

    const createResize = useCallback((header: Header<T, unknown>) => {
        return (
            <div
                {...{
                    onMouseDown: header.getResizeHandler(),
                    onTouchStart: header.getResizeHandler(),
                    className: `resizer ${header.column.getIsResizing() ? 'isResizing' : ''}`,
                    style: {
                        transform: '',
                    },
                }}
            />
        );
    }, []);

    const createSorted = useCallback(
        (_table: Table<T>, header: Header<T, unknown>) => {
            // Get the actual sortingOptionName used in state
            const sortingOptionName = header.column.columnDef.meta?.sortingOptionName ?? header.column.id;

            const found = sorting.find((sort) => sort.id === sortingOptionName);
            let isSorted: boolean | SortDirection = false;

            if (found) {
                if (found.desc) {
                    isSorted = SortDirection.DESCENDING;
                } else {
                    isSorted = SortDirection.ASCENDING;
                }
            }

            return (
                <>
                    <div
                        {...{
                            className: 'headerText cursor-pointer select-none',
                            ...(onSortChange
                                ? { onClick: () => onSortChange(header) }
                                : { onClick: header.column.getToggleSortingHandler() }),
                        }}
                    >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {isSorted === 'asc' && <span className="material-icons">arrow_drop_up</span>}
                        {isSorted === 'desc' && <span className="material-icons">arrow_drop_down</span>}
                    </div>
                </>
            );
        },
        [onSortChange, flexRender, sorting],
    );

    const createHeaders = useCallback(() => {
        return (
            <tr>
                {reactTable.getFlatHeaders().map((header) => {
                    const headerName = header.column.id.split('_').slice(-1)[0];
                    return (
                        <th
                            key={header.id}
                            colSpan={header.colSpan}
                            style={{ width: header.getSize() }}
                            className={`header-cell header-cell-${headerName}`}
                        >
                            {header.column.getCanSort() ? (
                                createSorted(reactTable, header)
                            ) : (
                                <div className="headerText">
                                    {flexRender(header.column.columnDef.header, header.getContext())}
                                </div>
                            )}
                            {isResizable && createResize(header)}
                            {header.column.getCanFilter() ? (
                                <div>
                                    <FilterOption
                                        column={header.column}
                                        onChange={onFilterChange || header.column.getAutoFilterFn}
                                    />
                                </div>
                            ) : null}
                        </th>
                    );
                })}
            </tr>
        );
    }, [reactTable, isResizable, flexRender, createResize, onFilterChange, createSorted]);

    // Check on mount and after a fetch to see if the table is already scrolled to the bottom
    // and immediately needs to fetch more data
    const handleScroll: React.UIEventHandler<HTMLDivElement> = (e) => {
        const target = e.target as HTMLDivElement;
        fetchMoreOnBottomReached(target);
    };

    const handleExport = async () => {
        const rows = reactTable.getRowModel().rows;
        const columns = reactTable.getAllColumns();
        exportTableData(columns, rows, exportFileName ? `${exportFileName}` : 'FMTP_Table_Data');
        setIsExporting(false);
    };

    return (
        <>
            <div style={{ display: 'flex', alignItems: 'center', paddingBottom: '5px', paddingTop: '10px' }}>
                {exportDataAuthorized &&
                    enableExport &&
                    (isExporting ? (
                        <Button size="small" variant="contained" type="button" disabled className="export-button">
                            <LoadingIndicator message="Exporting" />
                        </Button>
                    ) : (
                        <Button
                            size="medium"
                            variant="contained"
                            className="export-button"
                            onClick={handleExport}
                            disabled={isExporting}
                        >
                            Export As CSV
                        </Button>
                    ))}
                <Typography variant="body2" sx={{ color: 'var(--nav-text-color)', marginLeft: 'auto' }}>
                    {totalResults && `Showing ${totalResults} results`}
                </Typography>
            </div>
            <div ref={tableContainerRef} id="table-container" onScroll={handleScroll} style={containerStyles}>
                <table
                    className="dataTable"
                    style={{
                        display: `${showTable ? 'table' : 'none'}`,
                        // 'separate' prevents header borders from disappearing when virtual rows need to re-render
                        borderCollapse: `${virtualizer ? 'separate' : 'collapse'}`,
                    }}
                    ref={tableRef}
                >
                    <thead>{createHeaders()}</thead>
                    <tbody>{createBody()}</tbody>
                </table>

                {/* Overlay when fetching */}
                {/* {showLoading && isFetching && (
                    <div className="table-loading-indicator">
                        <LoadingIndicator message={loadingIndicatorMessage} />
                    </div>
                )} */}
                {!isFetching && !data.length && <div className="noData">No Data Found</div>}
            </div>
        </>
    );
};

export default FmtpTable;
